/**
 * Tahap 6B — Halaman Integrasi Sales/Purchase ↔ Inventory & Transfer Order.
 * Diekstrak dari InventoryPages.js (Tahap 7B cleanup) — behavior tidak berubah.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatDateID } from "@/lib/format";
import { PlusCircle } from "lucide-react";
import { NoAccessBanner } from "./InventoryPages";

const INV_PERMS_BY_ROLE = {
  super_admin: ["*"],
  gudang: ["INV_LIHAT", "INV_TRANSAKSI", "INV_TRANSFER", "INV_ADJUSTMENT", "INV_OPNAME", "INV_BATCH", "INV_KARTU_STOK"],
  akuntansi: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT", "INV_INTEGRITY", "INV_OPENING_BALANCE"],
  keuangan: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT"],
  auditor: ["INV_LIHAT", "INV_KARTU_STOK", "INV_INTEGRITY", "INV_HPP_LIHAT"],
  direktur_utama: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT", "INV_INTEGRITY", "INV_OPNAME_SETUJUI"],
  direktur_operasional: ["INV_LIHAT", "INV_KARTU_STOK", "INV_OPNAME_SETUJUI"],
  perpajakan: ["INV_LIHAT"],
  viewer: ["INV_LIHAT"],
};

function useCan() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = INV_PERMS_BY_ROLE[role] || [];
  const isSuper = role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

function Crumb({ path }) {
  return <div className="text-xs text-slate-500 mb-2">{path.join(" › ")}</div>;
}

/* ============ Integritas 6B ============ */
export function Integrity6BPage() {
  const can = useCan();
  const canView = can("INV_INTEGRITY");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!canView) return;
    api.get("/inventory/integrity-6b")
      .then(({ data }) => setD(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [canView]);
  const badgeCls = (s) => s === "SESUAI" ? "bg-emerald-600" : s === "PERLU_DITINJAU" ? "bg-amber-500" : s === "ERROR" ? "bg-rose-600" : "bg-slate-500";
  if (!canView) return (
    <div className="space-y-6 page-fade" data-testid="inv-integrity-6b-page">
      <div><Crumb path={["Persediaan", "Integritas Integrasi (6B)"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Integritas Integrasi</h1></div>
      <NoAccessBanner perm="INV_INTEGRITY" />
    </div>
  );
  return (
    <div className="space-y-6 page-fade" data-testid="inv-integrity-6b-page">
      <div>
        <Crumb path={["Persediaan", "Integritas Integrasi (6B)"]} />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Integritas Integrasi Penjualan/Pembelian ↔ Persediaan</h1>
          {d && <Badge className={badgeCls(d.overall)} data-testid="inv-int6b-overall">{d.overall}</Badge>}
        </div>
        <p className="text-sm text-slate-500 mt-2">Rekonsiliasi 6 titik: qty Pembelian↔Penerimaan, qty Penjualan↔Pengeluaran, HPP↔GL HPP, Retur↔Transaksi Sumber, Transfer Out↔In, Barang Dalam Perjalanan.</p>
      </div>
      <Card><CardContent className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Cek</TableHead><TableHead>Ekspektasi</TableHead><TableHead>Aktual</TableHead><TableHead>Selisih / Mismatch</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(d?.checks || []).map((c, i) => (
              <TableRow key={i} data-testid={`inv-int6b-row-${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell className="font-mono-num">{c.expected !== undefined ? c.expected : "-"}</TableCell>
                <TableCell className="font-mono-num">{c.actual !== undefined ? c.actual : "-"}</TableCell>
                <TableCell className="font-mono-num">{c.mismatch_count !== undefined ? `${c.mismatch_count}×` : (c.difference !== undefined ? c.difference : "-")}</TableCell>
                <TableCell><Badge className={badgeCls(c.status)}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {d && <div className="text-xs text-slate-500">Digenerate {formatDateID(d.generated_at)}</div>}
    </div>
  );
}

/* ============ Transfer Order FSM (DRAFT → DIKIRIM → DITERIMA) ============ */
export function TransferOrderPage() {
  const can = useCan();
  const canView = can("INV_TRANSFER");
  const canWrite = can("INV_TRANSFER");
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ sku: "", from_warehouse: "", to_warehouse: "", batch_no: "", qty: 0, date: new Date().toISOString().slice(0, 10) });
  const load = async () => {
    if (!canView) return;
    try { const { data } = await api.get("/inventory/transfer-orders"); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [canView]);
  if (!canView) return (
    <div className="space-y-6 page-fade" data-testid="inv-transfer-order-page">
      <div><Crumb path={["Persediaan", "Transfer Order"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Transfer Order (Antar Gudang)</h1></div>
      <NoAccessBanner perm="INV_TRANSFER" />
    </div>
  );
  const create = async () => {
    try {
      await api.post("/inventory/transfer-orders", { ...form, qty: parseFloat(form.qty) });
      toast.success("Transfer Order berhasil dibuat (DRAFT)");
      setForm({ ...form, qty: 0 });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const kirim = async (id) => {
    try { await api.post(`/inventory/transfer-orders/${id}/kirim`); toast.success("Transfer dikirim"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const terima = async (id) => {
    try { await api.post(`/inventory/transfer-orders/${id}/terima`); toast.success("Transfer diterima"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const badgeCls = (s) => s === "DRAFT" ? "bg-slate-500" : s === "DIKIRIM" ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div className="space-y-6 page-fade" data-testid="inv-transfer-order-page">
      <div><Crumb path={["Persediaan", "Transfer Order"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Transfer Order (Antar Gudang)</h1>
        <p className="text-sm text-slate-500 mt-2">Alur DRAFT → DIKIRIM → DITERIMA. Barang Dalam Perjalanan (BDP · COA 1302) menampung selama fase DIKIRIM.</p>
      </div>
      {canWrite && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="tord-sku" /></div>
            <div><Label>Dari Gudang</Label><Input value={form.from_warehouse} onChange={(e) => setForm({ ...form, from_warehouse: e.target.value })} data-testid="tord-from" /></div>
            <div><Label>Ke Gudang</Label><Input value={form.to_warehouse} onChange={(e) => setForm({ ...form, to_warehouse: e.target.value })} data-testid="tord-to" /></div>
            <div><Label>Batch (opsional)</Label><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} data-testid="tord-batch" /></div>
            <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="tord-qty" /></div>
            <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tord-date" /></div>
          </div>
          <Button onClick={create} data-testid="tord-create"><PlusCircle className="h-4 w-4 mr-2" />Buat Transfer Order</Button>
        </CardContent></Card>
      )}
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>SKU</TableHead><TableHead>Dari</TableHead><TableHead>Ke</TableHead><TableHead>Qty</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {list.map((t) => (
              <TableRow key={t.id} data-testid={`tord-row-${t.id}`}>
                <TableCell className="font-mono-num">{t.number}</TableCell>
                <TableCell>{formatDateID(t.date)}</TableCell>
                <TableCell>{t.sku}</TableCell>
                <TableCell>{t.from_warehouse}</TableCell>
                <TableCell>{t.to_warehouse}</TableCell>
                <TableCell className="font-mono-num">{t.qty}</TableCell>
                <TableCell><Badge className={badgeCls(t.status)}>{t.status}</Badge></TableCell>
                <TableCell className="space-x-2">
                  {canWrite && t.status === "DRAFT" && <Button size="sm" onClick={() => kirim(t.id)} data-testid={`tord-kirim-${t.id}`}>Kirim</Button>}
                  {canWrite && t.status === "DIKIRIM" && <Button size="sm" onClick={() => terima(t.id)} data-testid={`tord-terima-${t.id}`}>Terima</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
